import React, { useCallback, useContext, useMemo, useState } from 'react';
import { ActivityIndicator, Modal, Pressable, StyleSheet, Text, View } from 'react-native';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { GlobalStateContext } from '../../../components/general/GlobalStateProvider';
import {
    createBankSetupIntent,
    createSetupIntent,
    detachPaymentMethod,
    getOrCreateCustomer,
    getStripePublishableKey,
    listPaymentMethods,
} from '../../../data/StripeService';

type StripePaymentMethod = any;
type SetupMethodType = 'card' | 'us_bank_account';

interface CreditCardManagerProps {
    onMethodsChange?: (methods: StripePaymentMethod[]) => void;
}

const formatBrand = (pm: StripePaymentMethod) => {
    if (pm?.type === 'card') return (pm.card?.brand || 'card').toUpperCase();
    return (pm?.us_bank_account?.bank_name || 'BANK').toUpperCase();
};

const formatMaskedValue = (pm: StripePaymentMethod) => {
    if (pm?.type === 'card') return `•••• ${pm.card?.last4 || ''}`;
    return `•••• ${pm?.us_bank_account?.last4 || ''}`;
};

const SetupIntentForm: React.FC<{
    onSuccess: () => Promise<void> | void;
    onCancel: () => void;
}> = ({ onSuccess, onCancel }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    const handleSave = async () => {
        if (!stripe || !elements) return;
        setSubmitting(true);
        setError('');
        try {
            const result = await stripe.confirmSetup({
                elements,
                redirect: 'if_required',
            });

            if (result.error) {
                throw new Error(result.error.message || 'Unable to save payment method.');
            }

            await onSuccess();
        } catch (e: any) {
            setError(e?.message || 'Unable to save payment method.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <View>
            <PaymentElement options={{ layout: 'tabs' }} />

            {!!error && <Text style={styles.errorText}>{error}</Text>}

            <View style={styles.modalButtons}>
                <Pressable onPress={onCancel} style={styles.modalButtonGhost} disabled={submitting}>
                    <Text style={styles.modalButtonGhostText}>Cancel</Text>
                </Pressable>
                <Pressable onPress={handleSave} style={[styles.modalButtonPrimary, submitting && styles.disabled]} disabled={submitting}>
                    <Text style={styles.modalButtonPrimaryText}>{submitting ? 'Saving...' : 'Save payment method'}</Text>
                </Pressable>
            </View>
        </View>
    );
};

const CreditCardManager: React.FC<CreditCardManagerProps> = ({ onMethodsChange }) => {
    const { user } = useContext(GlobalStateContext);
    const [customerId, setCustomerId] = useState<string | null>(null);
    const [paymentMethods, setPaymentMethods] = useState<StripePaymentMethod[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [loadFailed, setLoadFailed] = useState(false);
    const [setupType, setSetupType] = useState<SetupMethodType>('card');
    const [clientSecret, setClientSecret] = useState('');
    const [showSetupModal, setShowSetupModal] = useState(false);

    const userId = user?.userId || '';
    const stripePublishableKey = getStripePublishableKey();
    const stripePromise = useMemo(() => {
        if (!stripePublishableKey) return null;
        return loadStripe(stripePublishableKey);
    }, [stripePublishableKey]);
    const isStripeConfigured = !!stripePublishableKey;

    const refreshPaymentMethods = useCallback(async () => {
        if (!userId) return;
        setLoading(true);
        setLoadFailed(false);
        try {
            const customer = await getOrCreateCustomer({
                userId,
                email: user?.email,
                name: `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || user?.displayName,
            });
            setCustomerId(customer.customerId);

            const [{ paymentMethods: cards }, { paymentMethods: banks }] = await Promise.all([
                listPaymentMethods(customer.customerId, 'card'),
                listPaymentMethods(customer.customerId, 'us_bank_account'),
            ]);

            const merged = [...(cards || []), ...(banks || [])];
            setPaymentMethods(merged);
            if (onMethodsChange) onMethodsChange(merged);
        } catch (e) {
            // Keep the screen usable even when Stripe endpoints are temporarily unavailable.
            setPaymentMethods([]);
            if (onMethodsChange) onMethodsChange([]);
            setLoadFailed(true);
        } finally {
            setLoading(false);
        }
    }, [onMethodsChange, user?.displayName, user?.email, user?.firstName, user?.lastName, userId]);

    React.useEffect(() => {
        const run = async () => {
            if (!isStripeConfigured || !userId) return;
            await refreshPaymentMethods();
        };
        run();
    }, [isStripeConfigured, refreshPaymentMethods, userId]);

    const cards = useMemo(() => paymentMethods.filter((pm) => pm?.type === 'card'), [paymentMethods]);
    const banks = useMemo(() => paymentMethods.filter((pm) => pm?.type === 'us_bank_account'), [paymentMethods]);

    const openSetup = async (type: SetupMethodType) => {
        if (!stripePromise) {
            setError('Stripe is not configured. Add EXPO_PUBLIC_STRIPE_PUBLISHABLE_KEY first.');
            return;
        }

        setError('');
        setLoading(true);
        try {
            const resolvedCustomerId =
                customerId ||
                (
                    await getOrCreateCustomer({
                        userId,
                        email: user?.email,
                        name: `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || user?.displayName,
                    })
                ).customerId;

            setCustomerId(resolvedCustomerId);
            setSetupType(type);

            const setup = type === 'card'
                ? await createSetupIntent(resolvedCustomerId)
                : await createBankSetupIntent(resolvedCustomerId);

            setClientSecret(setup.clientSecret || '');
            setShowSetupModal(true);
        } catch (e: any) {
            setError(e?.message || 'Unable to start payment setup.');
        } finally {
            setLoading(false);
        }
    };

    const handleRemoveMethod = async (paymentMethodId: string) => {
        setLoading(true);
        setError('');
        try {
            await detachPaymentMethod(paymentMethodId);
            await refreshPaymentMethods();
        } catch {
            setError('Unable to remove this payment method.');
        } finally {
            setLoading(false);
        }
    };

    if (!userId) {
        return (
            <View style={styles.emptyContainer}>
                <Text style={styles.emptySubtext}>Sign in to manage your payment methods.</Text>
            </View>
        );
    }

    return (
        <View style={styles.container}>
            {!isStripeConfigured && (
                <View style={styles.emptyContainer}>
                    <Text style={styles.emptySubtext}>Payments are being configured.</Text>
                </View>
            )}

            {isStripeConfigured && loadFailed && !loading && (
                <View style={styles.emptyContainer}>
                    <Text style={styles.emptySubtext}>We could not load saved payment methods right now.</Text>
                </View>
            )}

            <View style={styles.actionRow}>
                <Pressable
                    style={[styles.addButton, loading && styles.disabled]}
                    onPress={() => openSetup('card')}
                    disabled={loading}
                >
                    <MaterialCommunityIcons name="credit-card-plus-outline" size={18} color="#FFFFFF" />
                    <Text style={styles.addButtonText}>Add card</Text>
                </Pressable>

                <Pressable
                    style={[styles.addButtonSecondary, loading && styles.disabled]}
                    onPress={() => openSetup('us_bank_account')}
                    disabled={loading}
                >
                    <MaterialCommunityIcons name="bank-plus" size={18} color="#0F766E" />
                    <Text style={styles.addButtonSecondaryText}>Add bank account</Text>
                </Pressable>
            </View>

            {loading && (
                <View style={styles.loadingRow}>
                    <ActivityIndicator size="small" color="#0F766E" />
                    <Text style={styles.loadingText}>Working...</Text>
                </View>
            )}

            {!!error && <Text style={styles.errorText}>{error}</Text>}

            {isStripeConfigured && !loadFailed && !loading && cards.length === 0 && banks.length === 0 && (
                <View style={styles.emptyContainer}>
                    <MaterialCommunityIcons name="credit-card-off-outline" size={32} color="#94A3B8" />
                    <Text style={styles.emptyTitle}>No payment methods yet</Text>
                    <Text style={styles.emptySubtext}>Add a card or bank account to speed up checkout.</Text>
                </View>
            )}

            {isStripeConfigured && [...cards, ...banks].map((pm) => (
                <View key={pm.id} style={styles.paymentRow}>
                    <View style={styles.paymentRowLeft}>
                        <MaterialCommunityIcons
                            name={pm?.type === 'card' ? 'credit-card-outline' : 'bank-outline'}
                            size={20}
                            color="#111827"
                        />
                        <View style={styles.paymentTextWrap}>
                            <Text style={styles.brandText}>{formatBrand(pm)}</Text>
                            <Text style={styles.maskedText}>{formatMaskedValue(pm)}</Text>
                            {pm?.type === 'card' && (
                                <Text style={styles.metaText}>
                                    Expires {pm.card?.exp_month || ''}/{pm.card?.exp_year || ''}
                                </Text>
                            )}
                        </View>
                    </View>

                    <Pressable
                        style={[styles.removeButton, loading && styles.disabled]}
                        onPress={() => handleRemoveMethod(pm.id)}
                        disabled={loading}
                    >
                        <MaterialCommunityIcons name="trash-can-outline" size={16} color="#B91C1C" />
                        <Text style={styles.removeText}>Remove</Text>
                    </Pressable>
                </View>
            ))}

            <Modal visible={showSetupModal} transparent animationType="fade" onRequestClose={() => setShowSetupModal(false)}>
                <View style={styles.modalOverlay}>
                    <View style={styles.modalCard}>
                        <View style={styles.modalHeader}>
                            <Text style={styles.modalTitle}>
                                {setupType === 'card' ? 'Add credit card' : 'Add bank account'}
                            </Text>
                            <Pressable onPress={() => setShowSetupModal(false)}>
                                <MaterialCommunityIcons name="close" size={18} color="#475569" />
                            </Pressable>
                        </View>

                        {clientSecret && stripePromise ? (
                            <Elements stripe={stripePromise} options={{ clientSecret }}>
                                <SetupIntentForm
                                    onSuccess={async () => {
                                        setShowSetupModal(false);
                                        setClientSecret('');
                                        await refreshPaymentMethods();
                                    }}
                                    onCancel={() => {
                                        setShowSetupModal(false);
                                        setClientSecret('');
                                    }}
                                />
                            </Elements>
                        ) : (
                            <Text style={styles.errorText}>Unable to initialize Stripe setup.</Text>
                        )}
                    </View>
                </View>
            </Modal>
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        width: '100%',
        gap: 12,
    },
    actionRow: {
        flexDirection: 'row',
        gap: 10,
        flexWrap: 'wrap',
    },
    addButton: {
        height: 40,
        borderRadius: 10,
        backgroundColor: '#0F766E',
        paddingHorizontal: 14,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
    },
    addButtonText: {
        color: '#FFFFFF',
        fontWeight: '700',
        fontSize: 13,
    },
    addButtonSecondary: {
        height: 40,
        borderRadius: 10,
        borderWidth: 1,
        borderColor: '#0F766E',
        backgroundColor: '#ECFEFF',
        paddingHorizontal: 14,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
    },
    addButtonSecondaryText: {
        color: '#0F766E',
        fontWeight: '700',
        fontSize: 13,
    },
    loadingRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    loadingText: {
        color: '#475569',
        fontSize: 13,
    },
    emptyContainer: {
        alignItems: 'center',
        justifyContent: 'center',
        borderWidth: 1,
        borderColor: '#E2E8F0',
        borderStyle: 'dashed',
        borderRadius: 12,
        paddingVertical: 20,
        paddingHorizontal: 16,
        gap: 6,
    },
    emptyTitle: {
        color: '#0F172A',
        fontWeight: '700',
        fontSize: 15,
    },
    emptySubtext: {
        color: '#64748B',
        fontSize: 13,
        textAlign: 'center',
    },
    paymentRow: {
        borderWidth: 1,
        borderColor: '#E2E8F0',
        borderRadius: 10,
        paddingHorizontal: 12,
        paddingVertical: 10,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 10,
    },
    paymentRowLeft: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        flex: 1,
    },
    paymentTextWrap: {
        gap: 2,
    },
    brandText: {
        fontSize: 12,
        fontWeight: '700',
        color: '#0F172A',
    },
    maskedText: {
        fontSize: 15,
        fontWeight: '700',
        color: '#0F172A',
    },
    metaText: {
        fontSize: 12,
        color: '#64748B',
    },
    removeButton: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        borderWidth: 1,
        borderColor: '#FCA5A5',
        backgroundColor: '#FEF2F2',
        borderRadius: 8,
        paddingHorizontal: 8,
        paddingVertical: 6,
    },
    removeText: {
        color: '#B91C1C',
        fontSize: 12,
        fontWeight: '700',
    },
    errorText: {
        color: '#DC2626',
        fontSize: 13,
    },
    modalOverlay: {
        flex: 1,
        backgroundColor: 'rgba(15, 23, 42, 0.45)',
        alignItems: 'center',
        justifyContent: 'center',
        paddingHorizontal: 16,
    },
    modalCard: {
        width: '100%',
        maxWidth: 520,
        backgroundColor: '#FFFFFF',
        borderRadius: 14,
        borderWidth: 1,
        borderColor: '#E2E8F0',
        padding: 16,
        gap: 12,
    },
    modalHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    modalTitle: {
        color: '#0F172A',
        fontWeight: '700',
        fontSize: 17,
    },
    modalButtons: {
        marginTop: 10,
        flexDirection: 'row',
        justifyContent: 'flex-end',
        gap: 8,
    },
    modalButtonGhost: {
        height: 40,
        borderRadius: 9,
        borderWidth: 1,
        borderColor: '#CBD5E1',
        paddingHorizontal: 14,
        alignItems: 'center',
        justifyContent: 'center',
    },
    modalButtonGhostText: {
        color: '#334155',
        fontWeight: '600',
    },
    modalButtonPrimary: {
        height: 40,
        borderRadius: 9,
        backgroundColor: '#0F766E',
        paddingHorizontal: 14,
        alignItems: 'center',
        justifyContent: 'center',
    },
    modalButtonPrimaryText: {
        color: '#FFFFFF',
        fontWeight: '700',
    },
    disabled: {
        opacity: 0.6,
    },
});

export default CreditCardManager;
